// Facts the site states in more than one place. Derive them; never type the number into copy.
// Client-safe (no server imports): used by client pages, server pages and metadata alike.
import { OFFERED_LANGUAGE_CODES, COMIC_LANGUAGE_CODES, offeredLanguages } from '@/utils/languages/languages';
import { PICTURE_BOOK_FLOW_STYLES, pictureBookStyleName } from '@/utils/manga/picture-book-style';
import { MANGA_SUB_STYLE_INFO } from '@/types/manga';
import { UNPAID_INACTIVE_DAYS, UNPAID_WARN_AFTER_DAYS, PAID_RETENTION_MONTHS } from '@/lib/books/file-retention-state';
import type { PublicPrices } from './public-prices';
import type { CatalogueStory } from '@/lib/catalogue/stories';
import { COMPANY_LINE, FOUNDER } from '@/lib/site/founder';
import { AI_LINE } from '@/lib/site/labels';

/** "a, b and c" */
const joinNames = (names: readonly string[]) =>
  names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/** Languages offered for new books. SUPPORTED_LANGUAGES (24) only keeps books already made working. */
export const LANGUAGE_COUNT = OFFERED_LANGUAGE_CODES.length;
export const LANGUAGE_NAMES_TEXT = joinNames(offeredLanguages().map((l) => l.name));
export const BOOK_LANGUAGE_REFUSAL = `Books are available in ${LANGUAGE_NAMES_TEXT} for now. Please choose one of these languages.`;

/** Picture-book art styles offered in the create flow. Comics have their own list (COMIC_STYLE_COUNT). */
export const STYLE_COUNT = PICTURE_BOOK_FLOW_STYLES.length;
export const PICTURE_BOOK_STYLE_NAMES: string[] = PICTURE_BOOK_FLOW_STYLES.map(pictureBookStyleName);
export const PICTURE_BOOK_STYLE_NAMES_TEXT = joinNames(PICTURE_BOOK_STYLE_NAMES);

// Writing system per supported language. Hindi and Marathi share Devanagari; every Latin-
// alphabet language counts once. A language missing from this map counts as its own script,
// so adding a language without updating the map can only over-count by that one entry.
const SCRIPT_BY_LANGUAGE: Record<string, string> = {
  en: 'Latin', es: 'Latin', fr: 'Latin', de: 'Latin', it: 'Latin', pt: 'Latin',
  nl: 'Latin', sv: 'Latin', da: 'Latin', no: 'Latin', fi: 'Latin',
  zh: 'Han', ja: 'Japanese', ko: 'Hangul',
  ar: 'Arabic', he: 'Hebrew', ru: 'Cyrillic',
  hi: 'Devanagari', mr: 'Devanagari', pa: 'Gurmukhi',
  te: 'Telugu', ta: 'Tamil', ml: 'Malayalam', kn: 'Kannada',
};

export const WRITING_SYSTEM_COUNT = new Set(
  offeredLanguages().map((l) => SCRIPT_BY_LANGUAGE[l.code] ?? `other:${l.code}`)
).size;

/** How long the cover preview takes in the picture-book and comic engine (measured 41-55 s). */
export const COVER_PREVIEW_TIME = 'under a minute';

/** How long the cover preview takes in the legacy create-book wizard. */
export const WIZARD_COVER_PREVIEW_TIME = 'about 1-2 minutes';

/** How long the story text takes to write in the create-book wizard (multi-character path). */
export const STORY_TEXT_TIME = 'about 1-2 minutes';

/** How long the free pre-purchase interior preview takes to paint (book preview page). */
export const INTERIOR_PREVIEW_TIME = 'about 2-3 minutes';

// ---------------------------------------------------------------------------------------
// Delivery promises. One source of truth for every page and email that states them.
// ---------------------------------------------------------------------------------------

/** When the digital PDF arrives after purchase. */
export const DIGITAL_DELIVERY = 'within 2 hours';
export const DIGITAL_DELIVERY_SENTENCE = `Your digital PDF is emailed to you ${DIGITAL_DELIVERY}.`;

/** Print production and shipping times (printed by our local print partner). */
export const PRINT_PRODUCTION_TIME = '1-2 business days';
export const PRINT_SHIPPING_TIME = '3-7 business days';
export const PRINT_SHIPPING_TIME_REMOTE = '10-15 business days';
export const PRINT_DELIVERY_SHORT = `Printed in ${PRINT_PRODUCTION_TIME}, delivered in ${PRINT_SHIPPING_TIME}`;
/** For an "Estimated delivery:" label. */
export const PRINT_DELIVERY_ESTIMATE =
  `${PRINT_DELIVERY_SHORT} in most countries (up to ${PRINT_SHIPPING_TIME_REMOTE} for remote areas)`;
export const PRINT_DELIVERY_SENTENCE =
  `Printed books are printed in ${PRINT_PRODUCTION_TIME}, then delivered in ${PRINT_SHIPPING_TIME} ` +
  `in most countries (up to ${PRINT_SHIPPING_TIME_REMOTE} for remote areas).`;
export const PRINT_SHIPPING_COST_SENTENCE =
  'Shipping is calculated at checkout from our local print partner, based on your address.';

/** The softcover print format (always sold). */
export const PRINT_FORMAT = 'A5 (148×210mm) saddle-stitched softcover';

/** The 8x8 hardcover (sold while HARDCOVER_ENABLED is on). */
export const HARDCOVER_FORMAT = '8×8 inch (200×200mm) hardcover with a matte laminated cover';

/** How many print formats the site sells, in one sentence. Pass whether the hardcover is on sale. */
export function printFormatsSentence(hardcover: boolean): string {
  return hardcover
    ? `Printed books come as an ${PRINT_FORMAT} or an ${HARDCOVER_FORMAT}.`
    : `Printed books come in one format: ${PRINT_FORMAT}.`;
}

// ---------------------------------------------------------------------------------------
// Photo retention. Only claim what the code does:
// - Free-cover photos: deleted after 30 days by the retention sweep in services/free-cover.ts
//   (RETENTION_MS, sweepStale), run from the storage-cleanup job.
// - Book-creation photos (and the face crops made from them): deleted by
//   services/book-photo-retention.ts (sweepBookPhotos), run from the same storage-cleanup job —
//   30 days after the book is delivered (paid books), 30 days after it was created (books never
//   bought), or 180 days after payment (paid books we could not deliver). A free-cover photo copied into a book follows the book rule.
//   The sweep ships in dry-run mode (BOOK_PHOTO_RETENTION_DRY_RUN); it must be set to "false" in
//   production for these sentences to be true.
// ---------------------------------------------------------------------------------------
export const FREE_COVER_PHOTO_RETENTION_DAYS = 30;
export const BOOK_PHOTO_RETENTION_DAYS = 30;
export const PRIVACY_CONTACT_EMAIL = 'support@joonoo.art';
export const BOOK_PHOTO_UNDELIVERED_RETENTION_DAYS = 180; // = UNDELIVERED_PURGE_DAYS in services/book-photo-retention.ts (a test asserts they match)
export const BOOK_PHOTO_RETENTION_SENTENCE =
  `Photos you upload for a book are kept in private storage and deleted automatically ${BOOK_PHOTO_RETENTION_DAYS} days after your book is delivered. ` +
  `If you don't buy the book, they are deleted ${BOOK_PHOTO_RETENTION_DAYS} days after you create it. ` +
  `If you buy a book that we have not been able to deliver, they are deleted ${BOOK_PHOTO_UNDELIVERED_RETENTION_DAYS} days after you pay.`;
export const PHOTO_EARLY_DELETION_SENTENCE =
  `You can ask us to delete them sooner at any time (${PRIVACY_CONTACT_EMAIL}).`;
export const FREE_COVER_PHOTO_RETENTION_SENTENCE =
  `Free-cover photos are deleted automatically after ${FREE_COVER_PHOTO_RETENTION_DAYS} days.`;
export const FREE_COVER_TO_BOOK_PHOTO_SENTENCE =
  `If you turn your cover into a book, a copy of the photo is kept with that book and deleted on the book's schedule ` +
  `(${BOOK_PHOTO_RETENTION_DAYS} days after delivery, ${BOOK_PHOTO_RETENTION_DAYS} days after creation if the book isn't bought, or ${BOOK_PHOTO_UNDELIVERED_RETENTION_DAYS} days after payment if a paid book can't be delivered).`;
export const PHOTO_RETENTION_SHORT =
  `Book photos are deleted automatically ${BOOK_PHOTO_RETENTION_DAYS} days after your book is delivered, or ${BOOK_PHOTO_RETENTION_DAYS} days after creation if it isn't bought (${BOOK_PHOTO_UNDELIVERED_RETENTION_DAYS} days after payment if a paid book can't be delivered); free-cover photos after ${FREE_COVER_PHOTO_RETENTION_DAYS} days.`;

// ---------------------------------------------------------------------------------------
// Brand images. The original logo PNG is 2.4 MB: never use it as a social card or JSON-LD logo.
// Rebuild both with: node scripts/make-brand-images.mjs
// ---------------------------------------------------------------------------------------
export const OG_IMAGE = {
  url: 'https://joonoo.art/images/og/joonoo-og-1200x630.jpg',
  width: 1200,
  height: 630,
  alt: "Joonoo.art personalized children's storybooks",
} as const;
export const ORG_LOGO_URL = 'https://joonoo.art/images/logo/joonoo-logo-600.png';

/**
 * Social profiles the owner has confirmed exist and are run by Joonoo. Empty = none shown anywhere
 * (no sameAs in the Organization schema, no footer icons). The /joonooart profiles are NOT confirmed
 * as of 2026-10-08; add a URL here only once it is.
 */
export const SOCIAL_PROFILES: readonly string[] = [];

// ---- Book file retention (services/book-file-retention.ts) ----
// Periods come from lib/books/file-retention-state.ts so the copy and the job cannot drift.
// Approved copy 2026-10-10; the privacy policy and the FAQ render these sentences verbatim.
export const BOOK_FILES_UNPAID_PERIOD = `${UNPAID_INACTIVE_DAYS} days`;
export const BOOK_FILES_PAID_PERIOD = `${PAID_RETENTION_MONTHS} months`;
/** The reminder email goes out this long before an unbought preview is deleted. */
export const BOOK_FILES_REMINDER_LEAD = (UNPAID_INACTIVE_DAYS - UNPAID_WARN_AFTER_DAYS) === 7
  ? 'a week' : `${UNPAID_INACTIVE_DAYS - UNPAID_WARN_AFTER_DAYS} days`;

/** Privacy policy section 8 (rendered after a bold "Book files."). */
export const BOOK_FILES_RETENTION_SENTENCE =
  `If you don't buy a book, its preview (cover, illustrations and preview pages) is deleted after ${BOOK_FILES_UNPAID_PERIOD} without activity. ` +
  `If we have your email address we'll remind you about ${BOOK_FILES_REMINDER_LEAD} before; opening or ordering the book keeps it. ` +
  `For books you buy, the illustrations, cover and PDF files are kept for ${BOOK_FILES_PAID_PERIOD} after your order is delivered so you can view, download and reorder them, and are then deleted. ` +
  `Please download your PDF to keep your own copy. Contact support@joonoo.art if you need a book's files kept longer.`;

/** FAQ answers. */
export const BOOK_FILES_RETENTION_FAQ =
  `Unbought previews are deleted after ${BOOK_FILES_UNPAID_PERIOD} without activity (we email you first when we can). ` +
  `Purchased books' illustrations and PDFs are kept for ${BOOK_FILES_PAID_PERIOD} after delivery, so download your PDF to keep it.`;

// ---------------------------------------------------------------------------------------
// Guarantee windows (docs/superpowers/plans/2026-10-08-conversion-trust.md, feature C).
// Print window counts from orders.delivered_at (Gelato delivered webhook) when known; else from the later of
// Gelato's estimated delivery and paid_at + PRINT_DELIVERY_FALLBACK_DAYS (lenient by design).
// ---------------------------------------------------------------------------------------
export const LIKENESS_REDO_DAYS = 14;
export const PRINT_FAULT_REPORT_DAYS = 7;
export const PRINT_DELIVERY_FALLBACK_DAYS = 21;

// ---------------------------------------------------------------------------------------
// Guarantee copy (Task C4). Shown only while REDO_GUARANTEE_ENABLED is on (utils/feature-flags.ts);
// until then every surface keeps its previous wording. Pending the owner's legal review (Task C5).
// ---------------------------------------------------------------------------------------
/** Date the returns policy and terms last changed. Update by hand when they change. */
export const POLICY_UPDATED = '2026-10-10';
export const LIKENESS_REDO_SENTENCE =
  `If a likeness in your finished book disappoints you, ask within ${LIKENESS_REDO_DAYS} days of delivery (for a printed book, from the day it reaches you) and we'll redraw the illustrations once, free. Each book gets one redo.`;
export const PRINT_FAULT_SENTENCE =
  `If your printed book arrives damaged or misprinted, send us a photo within ${PRINT_FAULT_REPORT_DAYS} days of delivery and we'll send a replacement free.`;
export const DIGITAL_FINAL_SENTENCE =
  'Because every book is made for you as soon as you order, we don\'t otherwise offer refunds on digital books.';
export const STATUTORY_RIGHTS_SENTENCE = 'This does not affect your statutory rights.';
export const GUARANTEE_SHORT = `Free likeness redo within ${LIKENESS_REDO_DAYS} days`;
export const GUARANTEE_SENTENCE = `${LIKENESS_REDO_SENTENCE} ${PRINT_FAULT_SENTENCE} ${STATUTORY_RIGHTS_SENTENCE}`;

// ---------------------------------------------------------------------------------------
// Personalized comics (shown only while COMICS_ENABLED is on). Digital PDF only: never claim print.
// ---------------------------------------------------------------------------------------
export const COMIC_STYLE_COUNT = Object.keys(MANGA_SUB_STYLE_INFO).length;
export const COMIC_PAGE_COUNTS_TEXT = '16 pages (8 in lower-income countries)';
/** Comics use the same offered languages as picture books. */
export const COMIC_LANGUAGE_COUNT = COMIC_LANGUAGE_CODES.length;
export const COMIC_LANGUAGE_NAMES_TEXT = LANGUAGE_NAMES_TEXT;
export const COMIC_STYLE_NAMES: string[] = Object.values(MANGA_SUB_STYLE_INFO).map((i) => i.name);
export const COMIC_STYLE_NAMES_TEXT = joinNames(COMIC_STYLE_NAMES);
export const COMIC_LANGUAGE_REFUSAL = `Comics are available in ${COMIC_LANGUAGE_NAMES_TEXT} for now. Please choose one of these languages.`;
/** `squareOn` = COMIC_SQUARE_ENABLED (callers pass it explicitly; only pages and routes read the flag). */
export const comicPageCountsText = (squareOn: boolean) =>
  squareOn ? `${COMIC_PAGE_COUNTS_TEXT}, or 24 pages square` : COMIC_PAGE_COUNTS_TEXT;
/** `printOn` = COMIC_PRINT_ENABLED. The constants below are the printOn=false values. */
export const comicFormatText = (printOn: boolean) =>
  printOn ? 'Digital PDF, or a printed A5 comic with the PDF included' : 'Digital PDF only (no printed edition yet)';
export const comicSentence = (printOn: boolean, squareOn: boolean = false) =>
  `Personalized comics come in ${COMIC_STYLE_COUNT} manga and comic-book styles in ${COMIC_LANGUAGE_COUNT} languages, ${comicPageCountsText(squareOn)}, ${
    printOn
      ? `delivered as a digital PDF ${DIGITAL_DELIVERY}, or as a printed A5 comic with the PDF included`
      : `delivered as a digital PDF ${DIGITAL_DELIVERY}`
  }${printOn ? '.' : '; comics are not printed yet.'}`;
export const COMIC_FORMAT = comicFormatText(false);
export const COMIC_SENTENCE = comicSentence(false);

// ---------------------------------------------------------------------------------------
// The fact block (spec §10): the site's key facts in one place, as plain sentences. llms.txt "Key facts" is exactly
// this list, and the FAQ's styles answer reads it. Client-safe: type-only imports plus two import-free modules.
// Prices only from getPublicPrices() (passed in): with `prices: null` no sentence carries a price.
// ---------------------------------------------------------------------------------------

export type FactKey =
  | 'what' | 'ai' | 'preview' | 'comic-styles' | 'picture-book-styles' | 'titles' | 'write-your-own' | 'languages'
  | 'formats-prices' | 'dedication' | 'founder' | 'delivery' | 'photos' | 'ships';
export type Fact = { key: FactKey; text: string };
export type FactBlockInput = {
  prices: PublicPrices | null;
  /** Candidate titles; only launch-ready ones are named, comics only while `comics` is on, comics first. */
  stories: readonly CatalogueStory[];
  comics: boolean;
  comicPrint: boolean;
  comicSquare: boolean;
  hardcover: boolean;
};

const FACTS_SITE = 'https://joonoo.art';
const usd = (n: number) => `$${n.toFixed(2)}`;

function formatsAndPrices(o: FactBlockInput): string {
  const p = o.prices;
  const out: string[] = [];
  // Comics first (spec §6.1a).
  if (o.comics) {
    out.push(p
      ? `Comics (USD): ${usd(p.digital16)} for 16 pages (an 8-page comic is ${usd(p.digital8)} in lower-income countries), ${o.comicPrint ? 'digital PDF' : 'digital PDF only'}.`
      : `Comics: ${o.comicPrint ? 'digital PDF' : 'digital PDF only'}, 16 pages (8 in lower-income countries).`);
    if (o.comicPrint) {
      out.push(p
        ? `Printed comic (USD): a printed A5 comic is ${usd(p.print16)} for 16 pages, digital PDF included, plus shipping.`
        : 'Printed comic: a printed A5 comic is available, digital PDF included, plus shipping.');
    }
    if (o.comicSquare && p?.digital24 != null) {
      const hc = o.comicPrint && p.comicHardcover24 != null
        ? `, or ${usd(p.comicHardcover24)} as an 8×8 hardcover with the PDF included, plus shipping` : '';
      out.push(`Square comic (USD): a 24-page square comic is ${usd(p.digital24)} as a digital PDF${hc}.`);
    }
    out.push(comicSentence(o.comicPrint, o.comicSquare));
  }
  const hardcoverOn = o.hardcover && (p === null || p.hardcover != null);
  out.push(`Picture books come as a digital PDF. ${printFormatsSentence(hardcoverOn)}`);
  if (p) {
    out.push(`Picture books (USD): digital PDF ${usd(p.digital16)} for 16 pages (an 8-page edition is ${usd(p.digital8)} in lower-income countries); printed book ${usd(p.print16)} for 16 pages or ${usd(p.print24)} for 24 pages, digital PDF included, plus shipping.`);
    if (hardcoverOn && p.hardcover != null) {
      out.push(`Hardcover (USD): an 8×8 hardcover is ${usd(p.hardcover)} for 16 pages${p.hardcover24 != null ? ` or ${usd(p.hardcover24)} for 24 pages` : ''}, digital PDF included, plus shipping.`);
    }
  }
  out.push(`Current prices: ${FACTS_SITE}/pricing`);
  return out.join(' ');
}

/** The fact block, in reading order. Keys are unique; `comic-styles` and `titles` are left out when they'd be empty. */
export function buildFactBlock(o: FactBlockInput): Fact[] {
  const titles = o.stories
    .filter((s) => s.launchReady && (o.comics || s.product !== 'comic'))
    .sort((a, b) => (a.product === b.product ? 0 : a.product === 'comic' ? -1 : 1));
  const facts: (Fact | null)[] = [
    {
      key: 'what',
      text: `Upload one family photo and everyone in it is drawn into a personalized ${o.comics ? 'comic or picture book' : 'picture book'} with their real likeness.`,
    },
    { key: 'ai', text: AI_LINE },
    { key: 'preview', text: `The cover preview takes ${COVER_PREVIEW_TIME}.` },
    o.comics ? { key: 'comic-styles', text: `Comic styles (${COMIC_STYLE_COUNT}): ${COMIC_STYLE_NAMES_TEXT}.` } : null,
    { key: 'picture-book-styles', text: `Picture-book styles (${STYLE_COUNT}): ${PICTURE_BOOK_STYLE_NAMES_TEXT}.` },
    titles.length
      ? { key: 'titles', text: `Stories to choose from: ${titles.map((s) => `${s.name} (${s.product === 'comic' ? 'comic' : 'picture book'})`).join('; ')}.` }
      : null,
    { key: 'write-your-own', text: 'You can pick one of our stories or make up your own story.' },
    { key: 'languages', text: `Languages (${LANGUAGE_COUNT}): ${LANGUAGE_NAMES_TEXT}.` },
    { key: 'formats-prices', text: formatsAndPrices(o) },
    { key: 'dedication', text: o.comics ? 'Picture books and comics can carry a dedication at the front.' : 'Picture books can carry a dedication at the front.' },
    { key: 'founder', text: `joonoo.art was founded by ${FOUNDER.name} (${FACTS_SITE}/about). ${COMPANY_LINE}.` },
    { key: 'delivery', text: `${DIGITAL_DELIVERY_SENTENCE} ${PRINT_DELIVERY_SENTENCE}` },
    { key: 'photos', text: `Privacy: ${PHOTO_RETENTION_SHORT}` },
    { key: 'ships', text: 'Printed books are printed close to you by our print partners.' },
  ];
  return facts.filter((f): f is Fact => f !== null);
}

/** One fact's text, or '' when the block leaves it out. */
export function factText(block: readonly Fact[], key: FactKey): string {
  return block.find((f) => f.key === key)?.text ?? '';
}
